<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { messageStamp } from 'shared/helpers/timeHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ConversationsAnalysisAiApi from 'dashboard/api/conversationsAnalysisAi';
import AiAnalysisModal from './AiAnalysisModal.vue';
import AiAnalysisResultModal from './AiAnalysisResultModal.vue';

const { t } = useI18n();
const store = useStore();

const showModal = ref(false);
const isLoading = ref(false);
const isDeleting = ref(false);
const analyses = ref([]);
const activeDropdownId = ref(null);
const analysisToDelete = ref(null);
const deleteDialogRef = ref(null);
const showResultModal = ref(false);
const isLoadingResult = ref(false);
const selectedAnalysis = ref(null);

const POLL_INTERVAL = 60 * 1000;
const UNFINISHED_STATUSES = ['pending', 'processing'];

// design system semantics: amber = warning, teal = success, ruby = error, blue = info
const STATUS_CLASS = {
  pending: 'bg-n-amber-9/10 text-n-amber-11',
  processing: 'bg-n-blue-9/10 text-n-blue-11',
  ready: 'bg-n-teal-9/10 text-n-teal-11',
  failed: 'bg-n-ruby-9/10 text-n-ruby-11',
};

// silent refreshes come from the poller, so they must not swap the table for a spinner
// nor wipe the rows when a single request fails
const fetchAnalyses = async ({ silent = false } = {}) => {
  if (!silent) isLoading.value = true;
  try {
    const { data } = await ConversationsAnalysisAiApi.getAll();
    analyses.value = data;
  } catch {
    if (!silent) analyses.value = [];
  } finally {
    if (!silent) isLoading.value = false;
  }
};

onMounted(() => {
  fetchAnalyses();
  store.dispatch('agents/get');
});

const hasUnfinishedAnalyses = computed(() =>
  analyses.value.some(analysis => UNFINISHED_STATUSES.includes(analysis.status))
);

const { pause: pausePolling, resume: resumePolling } = useIntervalFn(
  () => fetchAnalyses({ silent: true }),
  POLL_INTERVAL,
  { immediate: false }
);

watch(hasUnfinishedAnalyses, unfinished =>
  unfinished ? resumePolling() : pausePolling()
);

const isResultGenerating = computed(
  () =>
    showResultModal.value &&
    UNFINISHED_STATUSES.includes(selectedAnalysis.value?.status)
);

const fetchResult = async ({ silent = false } = {}) => {
  if (!selectedAnalysis.value?.id) return;

  if (!silent) isLoadingResult.value = true;
  try {
    const { data } = await ConversationsAnalysisAiApi.show(
      selectedAnalysis.value.id
    );
    selectedAnalysis.value = data;

    const row = analyses.value.find(item => item.id === data.id);
    if (row) Object.assign(row, data);
  } catch {
    if (!silent) useAlert(t('REPORT.AI.RESULT.ERROR'));
  } finally {
    if (!silent) isLoadingResult.value = false;
  }
};

const { pause: pauseResultPolling, resume: resumeResultPolling } =
  useIntervalFn(() => fetchResult({ silent: true }), POLL_INTERVAL, {
    immediate: false,
  });

watch(isResultGenerating, generating =>
  generating ? resumeResultPolling() : pauseResultPolling()
);

const openModal = () => {
  showModal.value = true;
};

const openResult = async analysis => {
  selectedAnalysis.value = analysis;
  showResultModal.value = true;
  await fetchResult();
};

const statusLabels = computed(() => ({
  pending: t('REPORT.AI.TABLE.STATUS.PENDING'),
  processing: t('REPORT.AI.TABLE.STATUS.PROCESSING'),
  ready: t('REPORT.AI.TABLE.STATUS.READY'),
  failed: t('REPORT.AI.TABLE.STATUS.FAILED'),
}));

const menuItems = computed(() => [
  {
    label: t('REPORT.AI.MENU.DELETE'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const formattedDate = createdAt =>
  messageStamp(createdAt, 'MMM d, yyyy h:mm a');

const statusClass = status => STATUS_CLASS[status] ?? STATUS_CLASS.pending;

const closeDropdown = () => {
  activeDropdownId.value = null;
};

const toggleDropdown = id => {
  activeDropdownId.value = activeDropdownId.value === id ? null : id;
};

const handleAction = (analysis, { action }) => {
  closeDropdown();
  if (action !== 'delete') return;

  analysisToDelete.value = analysis;
  deleteDialogRef.value?.open();
};

const handleDeleteConfirm = async () => {
  const { id } = analysisToDelete.value;
  isDeleting.value = true;
  try {
    await ConversationsAnalysisAiApi.delete(id);
    analyses.value = analyses.value.filter(analysis => analysis.id !== id);
    useAlert(t('REPORT.AI.DELETE.SUCCESS'));
  } catch {
    useAlert(t('REPORT.AI.DELETE.ERROR'));
  } finally {
    isDeleting.value = false;
    deleteDialogRef.value?.close();
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex justify-end">
      <Button
        :label="t('REPORT.AI.NEW_ANALYSIS')"
        size="sm"
        @click="openModal"
      />
    </div>

    <div class="border rounded-lg border-n-weak">
      <table class="w-full text-sm table-fixed">
        <thead>
          <tr class="border-b border-n-weak">
            <th
              class="px-4 py-3 font-medium text-left text-n-slate-11 whitespace-nowrap"
            >
              {{ t('REPORT.AI.TABLE.HEADER.NAME') }}
            </th>
            <th
              class="px-4 py-3 font-medium text-left w-52 text-n-slate-11 whitespace-nowrap"
            >
              {{ t('REPORT.AI.TABLE.HEADER.CREATED_AT') }}
            </th>
            <th
              class="px-4 py-3 font-medium text-left w-36 text-n-slate-11 whitespace-nowrap"
            >
              {{ t('REPORT.AI.TABLE.HEADER.STATUS') }}
            </th>
            <th class="px-4 py-3 w-16">
              <span class="sr-only">
                {{ t('REPORT.AI.TABLE.HEADER.ACTIONS') }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="4" class="px-4 py-12">
              <div class="flex justify-center">
                <Spinner />
              </div>
            </td>
          </tr>
          <tr v-else-if="!analyses.length">
            <td colspan="4" class="px-4 py-12">
              <div class="flex flex-col items-center gap-1 text-center">
                <span class="font-medium text-n-slate-12">
                  {{ t('REPORT.AI.TABLE.EMPTY.TITLE') }}
                </span>
                <span class="text-n-slate-11">
                  {{ t('REPORT.AI.TABLE.EMPTY.DESCRIPTION') }}
                </span>
              </div>
            </td>
          </tr>
          <template v-else>
            <tr
              v-for="analysis in analyses"
              :key="analysis.id"
              class="border-b cursor-pointer border-n-weak last:border-b-0 hover:bg-n-alpha-1"
              @click="openResult(analysis)"
            >
              <td class="px-4 py-3">
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="font-medium truncate text-n-slate-12"
                    :title="analysis.name"
                  >
                    {{ analysis.name }}
                  </span>
                  <span
                    v-if="!analysis.read_at"
                    class="inline-flex items-center h-5 px-2 text-xs font-medium rounded-md bg-n-blue-3 text-n-blue-11 shrink-0"
                  >
                    {{ t('REPORT.AI.TABLE.NEW_BADGE') }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-n-slate-11 whitespace-nowrap">
                {{ formattedDate(analysis.created_at) }}
              </td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex items-center h-6 px-2 text-xs font-medium rounded-md whitespace-nowrap"
                  :class="statusClass(analysis.status)"
                >
                  {{ statusLabels[analysis.status] }}
                </span>
              </td>
              <td class="px-4 py-3" @click.stop>
                <div
                  v-on-clickaway="closeDropdown"
                  class="relative flex items-center justify-end group"
                >
                  <Button
                    icon="i-lucide-ellipsis-vertical"
                    color="slate"
                    size="xs"
                    class="rounded-md group-hover:bg-n-alpha-2"
                    @click="toggleDropdown(analysis.id)"
                  />
                  <DropdownMenu
                    v-if="activeDropdownId === analysis.id"
                    :menu-items="menuItems"
                    class="z-10 mt-1 top-full ltr:right-0 rtl:left-0"
                    @action="handleAction(analysis, $event)"
                  />
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <AiAnalysisModal v-model:show="showModal" @created="fetchAnalyses()" />

    <AiAnalysisResultModal
      v-model:show="showResultModal"
      :analysis="selectedAnalysis"
      :is-loading="isLoadingResult"
    />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('REPORT.AI.DELETE.TITLE')"
      :description="
        t('REPORT.AI.DELETE.DESCRIPTION', { name: analysisToDelete?.name })
      "
      :confirm-button-label="t('REPORT.AI.DELETE.CONFIRM')"
      :is-loading="isDeleting"
      @confirm="handleDeleteConfirm"
    />
  </div>
</template>
